import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Seo from '@/components/feature/Seo';
import NewsCard from '@/components/base/NewsCard';
import CategoryTag from '@/components/base/CategoryTag';
import { getCategoryById, getNewsById, getRelatedNews, formatDateTime, formatViews } from '@/utils/news';

export default function DetailPage() {
  const { newsId = '' } = useParams();
  const article = getNewsById(newsId);
  const [copied, setCopied] = useState(false);

  if (!article) {
    return (
      <div className="mx-auto max-w-[1400px] px-4 py-20 text-center md:px-6">
        <Seo title="新闻不存在-爆料网" description="抱歉，你访问的新闻爆料不存在或已下架。" path={`/detail/${newsId}`} />
        <i className="ri-file-unknow-line text-5xl text-foreground-300" />
        <h1 className="mt-4 font-heading text-2xl font-bold text-foreground-900">未找到这条新闻爆料</h1>
        <p className="mt-2 text-sm text-foreground-500">该新闻可能已下架，请返回首页浏览最新爆料。</p>
        <Link
          to="/"
          className="mt-6 inline-flex items-center gap-1 rounded-md bg-primary-600 px-4 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-700"
        >
          <i className="ri-home-4-line" />
          返回首页
        </Link>
      </div>
    );
  }

  const category = getCategoryById(article.category);
  const related = getRelatedNews(article, 4);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  return (
    <>
      <Seo
        title={`${article.title}-爆料网新闻详情`}
        description={article.summary}
        keywords={`${article.title},${article.tags.join(',')},爆料网,新闻爆料`}
        path={`/detail/${article.id}`}
        type="article"
      />

      <div className="mx-auto max-w-[1400px] px-4 py-6 md:px-6 md:py-8">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-[1fr_340px]">
          <article className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-8">
            <nav aria-label="面包屑导航" className="flex flex-wrap items-center gap-1 text-sm text-foreground-500">
              <Link to="/" className="inline-flex items-center gap-1 hover:text-primary-600">
                <i className="ri-home-4-line" />
                首页
              </Link>
              <i className="ri-arrow-right-s-line" />
              {category ? (
                <Link to={`/category/${category.id}`} className="hover:text-primary-600">
                  {category.name}
                </Link>
              ) : null}
              <i className="ri-arrow-right-s-line" />
              <span className="line-clamp-1 text-foreground-700">正文</span>
            </nav>

            <div className="mt-4 flex items-center gap-2">
              <CategoryTag categoryId={article.category} />
              {article.hot ? (
                <span className="inline-flex items-center gap-0.5 rounded-md bg-secondary-500 px-1.5 py-0.5 text-xs font-medium text-background-50">
                  <i className="ri-fire-fill" />
                  热点爆料
                </span>
              ) : null}
            </div>

            <h1 className="mt-3 font-heading text-2xl font-black leading-9 text-foreground-950 md:text-3xl md:leading-[2.75rem]">
              {article.title}
            </h1>

            <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 border-b border-background-200 pb-4 text-xs text-foreground-500">
              <span className="inline-flex items-center gap-1">
                <i className="ri-user-line" />
                {article.author}
              </span>
              <span className="inline-flex items-center gap-1">
                <i className="ri-newspaper-line" />
                {article.source}
              </span>
              <span className="inline-flex items-center gap-1">
                <i className="ri-time-line" />
                {formatDateTime(article.publishTime)}
              </span>
              <span className="inline-flex items-center gap-1">
                <i className="ri-eye-line" />
                {formatViews(article.views)} 次阅读
              </span>
            </div>

            <figure className="mt-6">
              <div className="aspect-[16/9] w-full overflow-hidden rounded-lg bg-background-200">
                <img
                  src={article.cover}
                  alt={article.title}
                  title={`${article.title} - 爆料网新闻爆料`}
                  className="h-full w-full object-cover object-top"
                />
              </div>
              <figcaption className="mt-2 text-center text-xs text-foreground-500">
                图为新闻相关实景示意图（演示内容）
              </figcaption>
            </figure>

            <div className="mt-6 space-y-5">
              {article.content.map((paragraph, i) => (
                <p key={`${article.id}-p-${i}`} className="text-base leading-8 text-foreground-800">
                  {paragraph}
                </p>
              ))}
            </div>

            <figure className="mt-6 overflow-hidden rounded-lg border border-background-200 bg-background-100">
              <div className="relative flex aspect-[16/9] items-center justify-center bg-primary-950">
                <img
                  src={article.cover}
                  alt={`${article.title} 视频报道封面`}
                  title={`${article.title} - 爆料网视频报道`}
                  className="absolute inset-0 h-full w-full object-cover object-top opacity-40"
                />
                <div className="relative flex flex-col items-center text-background-50">
                  <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-50/20">
                    <i className="ri-play-fill text-3xl" />
                  </span>
                  <span className="mt-3 text-sm">视频报道 · 演示说明</span>
                </div>
              </div>
              <figcaption className="px-4 py-3 text-xs leading-6 text-foreground-600">
                本条新闻支持图文与视频混合呈现，此处为视频播放区域的静态示意。正式版本可接入真实视频源进行播放。
              </figcaption>
            </figure>

            <div className="mt-6 flex flex-wrap items-center gap-2">
              {article.tags.map((tag) => (
                <Link
                  key={tag}
                  to={`/search?q=${encodeURIComponent(tag)}`}
                  className="rounded-md border border-background-200 bg-background-100 px-2.5 py-1 text-xs text-foreground-600 transition-colors hover:border-primary-300 hover:text-primary-600"
                >
                  #{tag}
                </Link>
              ))}
            </div>

            <div className="mt-8 flex flex-col gap-4 border-t border-background-200 pt-5 sm:flex-row sm:items-center sm:justify-between">
              <span className="text-sm text-foreground-500">分享这条爆料：</span>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={copyLink}
                  className="inline-flex items-center gap-1 whitespace-nowrap rounded-md border border-background-300 bg-background-100 px-3 py-2 text-sm text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600"
                >
                  <i className="ri-wechat-line" />
                  {copied ? '链接已复制' : '微信'}
                </button>
                <a
                  href={`https://service.weibo.com/share/share.php?title=${encodeURIComponent(article.title)}`}
                  target="_blank"
                  rel="nofollow noreferrer"
                  className="inline-flex items-center gap-1 whitespace-nowrap rounded-md border border-background-300 bg-background-100 px-3 py-2 text-sm text-foreground-700 transition-colors hover:border-secondary-300 hover:text-secondary-600"
                >
                  <i className="ri-weibo-line" />
                  微博
                </a>
                <a
                  href={`https://connect.qq.com/widget/shareqq/index.html?title=${encodeURIComponent(article.title)}`}
                  target="_blank"
                  rel="nofollow noreferrer"
                  className="inline-flex items-center gap-1 whitespace-nowrap rounded-md border border-background-300 bg-background-100 px-3 py-2 text-sm text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600"
                >
                  <i className="ri-qq-line" />
                  QQ
                </a>
              </div>
            </div>

            <div className="mt-8 rounded-lg bg-background-100 p-4 text-xs leading-6 text-foreground-500">
              <p>
                <strong className="text-foreground-700">版权声明：</strong>
                本文为爆料网原创/综合整理演示内容，版权归爆料网所有，未经授权禁止转载、摘编或以其他方式使用。
              </p>
              <p className="mt-2">
                <strong className="text-foreground-700">免责声明：</strong>
                文中信息均为演示示例，不构成任何事实陈述或建议。如涉及真实事件，请以官方发布为准。
              </p>
            </div>
          </article>

          <aside className="flex flex-col gap-6">
            <section className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
              <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
                <i className="ri-links-line text-primary-500" />
                相关推荐
              </h2>
              <div className="mt-3 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-1">
                {related.map((item) => (
                  <NewsCard key={item.id} article={item} variant="compact" />
                ))}
              </div>
            </section>

            {category ? (
              <section className="rounded-lg border border-primary-200 bg-primary-50 p-4 md:p-5">
                <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-primary-800">
                  <i className="ri-folder-3-line" />
                  所属分类
                </h2>
                <p className="mt-2 text-sm leading-6 text-primary-900/80">{category.intro}</p>
                <Link
                  to={`/category/${category.id}`}
                  className="mt-3 inline-flex items-center gap-1 whitespace-nowrap rounded-md bg-primary-600 px-3.5 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-700"
                >
                  进入{category.name}
                  <i className="ri-arrow-right-line" />
                </Link>
              </section>
            ) : null}
          </aside>
        </div>
      </div>
    </>
  );
}